import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  LeaveAdminUpdateDTO,
  LeaveDto,
  LeaveStatus,
} from "../../../../types/leaves.types";
import Table from "../../../shared/dataTable/index";
import { formatDate } from "../../../../utils/date";
import { Avatar, message } from "antd";
import LeaveStatusDropdown from "./LeaveStatusDropdown";
import { getFileUrl } from "../../../../utils/file.utils";
import { all_routes } from "../../../../router/all_routes";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import FileService from "../../../../services/file/file.service";
import { PermissionType } from "../../../../types/user.types";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import {
  exportToExcel,
  exportToPdf,
  normalizeTurkishChars,
  exportLeaveDetailsToPdf,
} from "../../../../utils/exportUtils";
import { Menu, Modal } from "antd";
import { useCompanyData } from "../../../../hooks/useCompanyData";
import leaveService from "../../../../services/leave/leave.service";

interface LeaveTableProps {
  leaves: LeaveDto[];
  onStatusChange: (leaveId: number, dto: LeaveAdminUpdateDTO) => void; // Update type
  onEdit?: (leave: LeaveDto) => void;
  /** Aynı satıra eklenecek (örn. Filtreler butonu) - Sayfa başına kayıt ve Ara ile aynı hizada */
  extraToolbar?: React.ReactNode;
}

export const LeaveTable: React.FC<LeaveTableProps> = ({
  leaves,
  onStatusChange,
  onEdit,
  extraToolbar,
}) => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { companyData } = useCompanyData();
  const [leaveBalanceByUser, setLeaveBalanceByUser] = useState<
    Record<number, Record<string, any>>
  >({});
  const [balanceLoadingByUser, setBalanceLoadingByUser] = useState<
    Record<number, boolean>
  >({});

  const userIds = useMemo(
    () =>
      Array.from(
        new Set(
          leaves
            .map((leave) => leave.user?.id)
            .filter((id): id is number => typeof id === "number")
        )
      ),
    [leaves]
  );

  useEffect(() => {
    const fetchMissingBalances = async () => {
      const missingUserIds = userIds.filter(
        (id) => !leaveBalanceByUser[id] && !balanceLoadingByUser[id]
      );

      if (!missingUserIds.length) return;

      setBalanceLoadingByUser((prev) => {
        const next = { ...prev };
        missingUserIds.forEach((id) => {
          next[id] = true;
        });
        return next;
      });

      await Promise.all(
        missingUserIds.map(async (userId) => {
          try {
            const response = await leaveService.getComprehensiveLeaveBalance(userId);
            const balance =
              response?.leaveBalance || response?.data?.leaveBalance || {};

            setLeaveBalanceByUser((prev) => ({
              ...prev,
              [userId]: balance,
            }));
          } catch {
            setLeaveBalanceByUser((prev) => ({
              ...prev,
              [userId]: {},
            }));
          } finally {
            setBalanceLoadingByUser((prev) => ({
              ...prev,
              [userId]: false,
            }));
          }
        })
      );
    };

    fetchMissingBalances();
  }, [userIds, leaveBalanceByUser, balanceLoadingByUser]);

  const columns = [
    {
      title: t("leaves.table.user"),
      dataIndex: ["user", "firstName"],
      render: (_: string | undefined, record: LeaveDto) => (
        <div className="d-flex align-items-center">
          {record.user && (
            <>
              {record.user?.profilePicture ? (
                <Avatar
                  src={getFileUrl(record.user.profilePicture)}
                  className="me-2"
                  size="large"
                />
              ) : (
                <Avatar
                  src={
                    record.user.gender === "male"
                      ? "/assets/img/man.png"
                      : "/assets/img/woman.png"
                  }
                  className="me-2"
                  size="large"
                />
              )}
              <span>
                <div className="ms-2">
                  <p className="text-dark mb-0">
                    <Link
                      to={`${all_routes.userLeaves}?user=${record.user?.id ?? ""}`}
                    >
                      {record.user?.firstName} {record.user?.lastName}
                    </Link>
                  </p>
                  <span className="fs-12">{record.user?.title}</span>
                </div>
              </span>
            </>
          )}
        </div>
      ),
      sorter: (a: any, b: any) =>
        a.user.firstName.length - b.user.firstName.length,
    },
    {
      title: t("leaves.table.leaveType"),
      dataIndex: "type",
      render: (text: string, record: LeaveDto) => (
        <div className="d-flex flex-column">
          <p className="fs-14 fw-medium d-flex align-items-center mb-0">
            {t(`leaveTypes.${record.type}`)}
          </p>
          <small
            className="text-muted d-block"
            style={{ fontSize: "11px", lineHeight: 1.2 }}
          >
            {(() => {
              const userId = record.user?.id;
              if (!userId) return "-";

              if (balanceLoadingByUser[userId]) {
                return "Bakiye yükleniyor...";
              }

              const typeBalance = leaveBalanceByUser[userId]?.[record.type];
              if (!typeBalance) return "Kalan bilgi yok";

              return `Kalan: ${typeBalance.remaining ?? 0} ${
                typeBalance.unit || "gün"
              } | Kullanılan: ${typeBalance.used ?? 0} ${
                typeBalance.unit || "gün"
              }`;
            })()}
          </small>
        </div>
      ),
      sorter: (a: any, b: any) => a.type.length - b.type.length,
    },
    {
      title: t("leaves.table.date"),
      dataIndex: "startDate",
      render: (_: string, record: LeaveDto) =>
        record.type === "hourly" ? (
          <p className="fs-14 mb-0">
            {formatDate(record.startDate)}
            <br />
            <small className="text-muted">
              {record.startTime} - {record.endTime}
            </small>
          </p>
        ) : (
          <p className="fs-14 mb-0">
            {formatDate(record.startDate)} -
            <br />
            {formatDate(record.endDate)}
          </p>
        ),
    },
    {
      title: t("leaves.table.description"),
      dataIndex: "reason", // Assuming `Description` is the field in your data
      render: (reason: string) =>
        reason.length > 30 ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip title={reason} placement="top">
                {reason}
              </Tooltip>
            }
          >
            <div className="position-relative">
              <p
                className="fs-14 mb-0"
                style={{
                  maxWidth: "100px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  paddingRight: "22px",
                }}
              >
                {reason}
                <span
                  className="text-primary"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "18px",
                    height: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    opacity: 0.8,
                  }}
                >
                  <i className="ti ti-info-circle" />
                </span>
              </p>
            </div>
          </OverlayTrigger>
        ) : (
          <p className="fs-14 mb-0">{reason}</p>
        ),
      sorter: (a: any, b: any) => a.reason.length - b.reason.length,
    },
    {
      title: t("Onay Süreci"),
      dataIndex: "approvalFlow",
      render: (_: any, record: LeaveDto) => {
        // Özel Durum: Son onaycı tarafından direkt onaylandıysa notu göster
        if (record.status === LeaveStatus.APPROVED && record.approvalHistory && record.approvalHistory.length > 0) {
          const lastHistoryItem = record.approvalHistory[record.approvalHistory.length - 1];
          if (lastHistoryItem.notes === "Hiyerarşideki son onaycı tarafından süreç atlanarak direkt onaylandı.") {
            return <div>{lastHistoryItem.notes}</div>;
          }
        }
        
        // Durum 1: Hiyerarşik İzin
        if (record.approvalFlow && record.approvalFlow.steps.length > 0) {
          const steps = record.approvalFlow.steps;
          
          const allApproved = steps.every(s => s.status === 'approved');
          if (allApproved) {
            return <div>Tüm onaylar tamamlandı</div>;
          }

          const historyElements: React.ReactNode[] = [];
          for (const step of steps) {
              const approverName = `${step.approver.firstName} ${step.approver.lastName}`;
              if (step.status === 'approved') {
                   historyElements.push(<div key={step.stepOrder}>{`${approverName} Onayladı`}</div>);
              } else if (step.status === 'rejected') {
                   historyElements.push(<div key={step.stepOrder}>{`${approverName} tarafından reddedildi`}</div>);
                   break; // Reddedildikten sonra göstermeyi durdur
              } else if (step.status === 'pending_approval' || step.status === 'pending') {
                   historyElements.push(<div key={step.stepOrder}>{`${approverName} onayı bekleniyor (${step.stepOrder}/${steps.length})`}</div>);
                   break; // Sıradaki onaycıyı bulduktan sonra dur
              }
          }
          return <div>{historyElements}</div>;
        }
    
        // Durum 2: Hiyerarşik Olmayan İzin
        if (record.status === LeaveStatus.APPROVED) {
          const processor = record.processedBy || record.approvedBy;
          return processor ? <div>{`${processor.firstName} ${processor.lastName} tarafından onaylandı`}</div> : <div>{'Onaylandı'}</div>;
        }
    
        if (record.status === LeaveStatus.REJECTED) {
          const processor = record.processedBy || record.rejectedBy;
          return processor ? <div>{`${processor.firstName} ${processor.lastName} tarafından reddedildi`}</div> : <div>{'Reddedildi'}</div>;
        }
    
        // Diğer durumlar için varsayılan
        return "-";
      },
    },
    {
      title: t("leaves.table.file"),
      dataIndex: "file",
      render: (_: string, record: LeaveDto) => (
        <p className="fs-14 mb-0">
          {record.documentUrl ? (
            <a
              className="dropdown-item"
              href="#"
              onClick={async (e) => {
                e.preventDefault();
                try {
                  await FileService.getInstance().viewFileAsBlob(record.documentUrl!);
                } catch (error) {
                  message.error('Belge görüntülenirken hata oluştu');
                }
              }}
            >
              Görüntüle
            </a>
          ) : (
            "-"
          )}
        </p>
      ),
    },
    {
      title: t("leaves.table.status"),
      dataIndex: "status", // Assuming `AdminStatus` is the field in your data
      render: (status: LeaveStatus, record: LeaveDto) => (
        <LeaveStatusDropdown
          canUpdateStatus={user?.permissions?.includes(PermissionType.EDIT_LEAVE)}
          status={status}
          record={record}
          onStatusChange={(dto) => onStatusChange(record.id, dto)}
          disabled={!canUpdateStatus(record)}
        />
      ),
    },
  ];

  // Actions sütununu sadece onEdit prop'u varsa ekle
  if (onEdit) {
    columns.push({
      title: "İşlemler",
      dataIndex: "actions",
      render: (text: string, record: LeaveDto) => (
        <div className="action-icon d-inline-flex">
          <Link
            to="#"
            className="me-2"
            onClick={(e) => {
              e.preventDefault();
              onEdit(record);
            }}
            title="İzin Talebini Düzenle"
          >
            <i className="ti ti-edit" />
          </Link>
          <Link
            to="#"
            className="me-2"
            onClick={async (e) => {
              e.preventDefault();
              Modal.confirm({
                title: 'PDF İndir',
                content: 'İzin detaylarını PDF olarak indirmek istediğinizden emin misiniz?',
                onOk: async () => {
                  await exportLeaveDetailsToPdf(record, companyData?.name);
                },
                okText: 'İndir',
                cancelText: 'İptal',
              });
            }}
            title="İzin Detaylarını PDF'e İndir"
          >
            <i className="ti ti-file-text" />
          </Link>
        </div>
      ),
    });
  }

  const searchByName = (record: LeaveDto, searchText: string) => {
    const text = searchText.trim().toLowerCase();
    if (!text) return true;
    const firstName = (record.user?.firstName ?? "").toLowerCase();
    const lastName = (record.user?.lastName ?? "").toLowerCase();
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName.includes(text) || firstName.includes(text) || lastName.includes(text);
  };

  return (
    <Table
      columns={columns}
      dataSource={leaves}
      Selection={false}
      showPageSize={false}
      extraToolbar={extraToolbar}
      searchFilterFn={searchByName}
      searchPlaceholder="İsim veya soyisim ile ara"
    />
  );
};

const canUpdateStatus = (leave: LeaveDto): boolean => {
  // Implement the logic to determine if the status can be updated
  return true; // Placeholder return, actual implementation needed
};
